import React, { useCallback, useEffect, useState } from 'react';
import { ClipboardCheck, Search, RefreshCw, Star, Share2, MessageSquareQuote, CheckCircle2, Hourglass, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { easerGet, errorMessage, fmtDate, fmtTime } from '../../lib/easerApi';
import { QualityControlDialog } from './qualite/QualityControlDialog';
import { QualityDetailDialog } from './qualite/QualityDetailDialog';
import { StarRating, fmtAvg, fmtDateTime } from './qualite/qcShared';
import { tagStyle, tagKey } from '../../lib/jobTags';

const DAYS = [[7, '7 jours'], [14, '14 jours'], [31, '31 jours']];
const STATUS = {
  in_progress: { label: 'En cours', cls: 'bg-[hsl(var(--accent))] text-white', icon: Hourglass },
  done: { label: 'Terminée', cls: 'bg-[hsl(var(--brand-sage))] text-white', icon: CheckCircle2 },
};

/** Onglet manager « Contrôle qualité » : interventions récentes (toute l'équipe) à contrôler + contrôles réalisés. */
export function EaserQualite({ user, onUnauthorized }) {
  const [view, setView] = useState('interventions'); // interventions | controles
  const [days, setDays] = useState(7);
  const [q, setQ] = useState('');
  const [sector, setSector] = useState('all'); // filtre secteur (tags métier Dolibarr des Easers : Clean, Green, DIY…)
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [controls, setControls] = useState(null);
  const [creating, setCreating] = useState(null); // intervention
  const [detail, setDetail] = useState(null); // control id

  const loadInterventions = useCallback(async () => {
    setError('');
    try {
      const res = await easerGet('/api/easer/qualite/interventions', { days });
      setData(res.data);
    } catch (err) {
      if (err?.response?.status === 401) return onUnauthorized();
      setError(errorMessage(err, 'Impossible de charger les interventions'));
      setData({ items: [] });
    }
  }, [days, onUnauthorized]);

  const loadControls = useCallback(async () => {
    try {
      const res = await easerGet('/api/easer/qualite', { limit: 100 });
      setControls(res.data.items || []);
    } catch (err) {
      if (err?.response?.status === 401) return onUnauthorized();
      setControls([]);
    }
  }, [onUnauthorized]);

  useEffect(() => { setData(null); loadInterventions(); }, [loadInterventions]);
  useEffect(() => { loadControls(); }, [loadControls]);

  const refreshAll = () => { setData(null); loadInterventions(); loadControls(); };
  const needle = q.trim().toLowerCase();
  const sectors = data?.sectors || [];
  const items = (data?.items || [])
    .filter((i) => sector === 'all' || (i.sectors || []).some((sct) => tagKey(sct) === sector))
    .filter((i) => !needle || `${i.label} ${i.client_name} ${i.easer_names.join(' ')} ${(i.sectors || []).join(' ')}`.toLowerCase().includes(needle));

  return (
    <div className="space-y-4" data-testid="easer-qualite">
      <div className="flex items-start gap-3">
        <span className="h-11 w-11 rounded-xl bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] flex items-center justify-center flex-shrink-0"><ClipboardCheck className="h-5 w-5" /></span>
        <div className="min-w-0 flex-1">
          <h2 className="font-['Space_Grotesk'] text-xl font-semibold leading-tight">Contrôle qualité</h2>
          <p className="text-sm text-muted-foreground">Évaluez une intervention en cours ou terminée : étoiles par critère, photos et compte rendu. Le contrôle est visible par les Easers concernés et compte double dans leur note.</p>
        </div>
        <Button variant="ghost" size="icon" className="h-11 w-11" onClick={refreshAll} aria-label="Actualiser" data-testid="qualite-refresh"><RefreshCw className="h-4 w-4" /></Button>
      </div>

      <Tabs value={view} onValueChange={setView}>
        <TabsList className="grid grid-cols-2 w-full h-11">
          <TabsTrigger value="interventions" data-testid="qualite-tab-interventions">À contrôler</TabsTrigger>
          <TabsTrigger value="controles" data-testid="qualite-tab-controles">Contrôles réalisés{controls?.length ? ` (${controls.length})` : ''}</TabsTrigger>
        </TabsList>
      </Tabs>

      {view === 'interventions' && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Client, Easer, prestation…" className="pl-9 h-11" data-testid="qualite-search" />
            </div>
            <div className="inline-flex rounded-lg border border-border p-0.5" role="radiogroup" aria-label="Période">
              {DAYS.map(([d, label]) => (
                <button key={d} type="button" role="radio" aria-checked={days === d} onClick={() => setDays(d)} data-testid={`qualite-days-${d}`}
                  className={`h-10 px-3 rounded-md text-xs font-semibold transition-colors ${days === d ? 'bg-[hsl(var(--primary))] text-white' : 'text-muted-foreground hover:bg-secondary'}`}>{label}</button>
              ))}
            </div>
          </div>

          {sectors.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5" role="radiogroup" aria-label="Secteur" data-testid="qualite-sector-filter">
              <button type="button" role="radio" aria-checked={sector === 'all'} onClick={() => setSector('all')} data-testid="qualite-sector-all"
                className={`h-9 px-3 rounded-full border text-xs font-semibold transition-colors ${sector === 'all' ? 'bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]' : 'border-border text-muted-foreground hover:bg-secondary'}`}>Tous secteurs</button>
              {sectors.map((sct) => {
                const key = tagKey(sct);
                const on = sector === key;
                const count = (data?.items || []).filter((i) => (i.sectors || []).some((x) => tagKey(x) === key)).length;
                return (
                  <button key={key} type="button" role="radio" aria-checked={on} onClick={() => setSector(on ? 'all' : key)} data-testid={`qualite-sector-${key}`}
                    className={`h-9 px-3 rounded-full border text-xs font-semibold inline-flex items-center gap-1.5 transition-colors ${on ? 'ring-2 ring-offset-1 ring-[hsl(var(--primary))]' : ''} ${tagStyle(sct).chip}`}>
                    <span className={`h-2 w-2 rounded-full ${tagStyle(sct).dot}`} /> {sct} <span className="opacity-70">{count}</span>
                  </button>
                );
              })}
            </div>
          )}

          {!data && <div className="space-y-2">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-20 rounded-xl" />)}</div>}
          {data && error && (
            <Card className="p-4 border-destructive/30" role="alert" data-testid="qualite-error"><p className="text-sm">{error}</p><Button variant="outline" className="mt-2 h-10" onClick={loadInterventions}>Réessayer</Button></Card>
          )}
          {data && !error && items.length === 0 && (
            <Card className="p-6 text-center text-sm text-muted-foreground" data-testid="qualite-empty">Aucune intervention commencée ou terminée sur cette période{needle ? ' pour cette recherche' : ''}{sector !== 'all' ? ' dans ce secteur' : ''}.</Card>
          )}
          {data && items.length > 0 && (
            <ul className="space-y-2" data-testid="qualite-interventions-list">
              {items.map((it) => {
                const st = STATUS[it.status] || STATUS.in_progress;
                return (
                  <li key={it.event_id}>
                    <Card className="p-3 flex items-start gap-3" data-testid={`qualite-intervention-${it.event_id}`}>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium leading-snug truncate">{it.label}</p>
                        <p className="text-sm text-muted-foreground truncate">{it.client_name || 'Client inconnu'}{it.client_town ? ` · ${it.client_town}` : ''}</p>
                        <p className="text-xs text-muted-foreground mt-0.5 flex flex-wrap items-center gap-x-2">
                          <span className="capitalize">{fmtDate(it.start, { weekday: 'short', day: 'numeric', month: 'short' })} {fmtTime(it.start)}</span>
                          <span>· {it.easer_names.join(', ') || 'Easer non affecté'}</span>
                          {(it.sectors || []).map((sct) => <Badge key={sct} className={`border text-[10px] ${tagStyle(sct).badge}`} data-testid={`qualite-intervention-sector-${it.event_id}-${tagKey(sct)}`}>{sct}</Badge>)}
                          <Badge className={`${st.cls} hover:${st.cls} text-[10px]`}><st.icon className="h-3 w-3 mr-1" /> {st.label}</Badge>
                        </p>
                      </div>
                      {it.control ? (
                        <Button variant="outline" size="sm" className="h-10 flex-shrink-0 border-[hsl(var(--brand-apricot))]/60" onClick={() => setDetail(it.control.id)} data-testid={`qualite-view-${it.event_id}`}>
                          <Star className="h-4 w-4 mr-1 fill-[hsl(var(--brand-apricot))] text-[hsl(var(--brand-apricot))]" /> {fmtAvg(it.control.average)}
                        </Button>
                      ) : (
                        <Button size="sm" className="h-10 flex-shrink-0 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white" onClick={() => setCreating(it)} data-testid={`qualite-create-${it.event_id}`}>
                          <ClipboardCheck className="h-4 w-4 mr-1" /> Contrôler
                        </Button>
                      )}
                    </Card>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}

      {view === 'controles' && (
        <>
          {controls === null && <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>}
          {controls && controls.length === 0 && <Card className="p-6 text-center text-sm text-muted-foreground" data-testid="qualite-controls-empty">Aucun contrôle qualité pour le moment. Choisissez une intervention dans « À contrôler ».</Card>}
          {controls && controls.length > 0 && (
            <ul className="space-y-2" data-testid="qualite-controls-list">
              {controls.map((c) => (
                <li key={c.id}>
                  <button type="button" onClick={() => setDetail(c.id)} className="w-full text-left" data-testid={`qualite-control-${c.id}`}>
                    <Card className="p-3 hover:border-[hsl(var(--primary))]/40 transition-colors">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-medium leading-snug truncate">{c.label}{c.client_name ? ` · ${c.client_name}` : ''}</p>
                          <p className="text-xs text-muted-foreground">{fmtDateTime(c.created_at)} · {c.easer_names?.join(', ')}{c.manager_name ? ` · par ${c.manager_name}` : ''}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="font-semibold">{fmtAvg(c.average)} / 5</p>
                          <StarRating value={c.average} size="h-3.5 w-3.5" />
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {c.share_with_client && <Badge className="border text-[10px] bg-[hsl(var(--brand-sky))]/40 text-[hsl(var(--brand-ink))] border-[hsl(var(--brand-sky))]"><Share2 className="h-3 w-3 mr-1" /> Client</Badge>}
                        {c.reply && <Badge className="border text-[10px] bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] border-[hsl(var(--primary))]/30"><MessageSquareQuote className="h-3 w-3 mr-1" /> Réponse Easer</Badge>}
                        {c.photos?.length > 0 && <Badge variant="outline" className="text-[10px]">{c.photos.length} photo{c.photos.length > 1 ? 's' : ''}</Badge>}
                        {c.read_by?.length > 0 && <Badge variant="outline" className="text-[10px] text-muted-foreground"><Eye className="h-3 w-3 mr-1" /> Lu</Badge>}
                      </div>
                    </Card>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      <QualityControlDialog open={!!creating} intervention={creating} onClose={() => setCreating(null)} onUnauthorized={onUnauthorized}
        onDone={() => { setCreating(null); refreshAll(); }} />
      <QualityDetailDialog open={!!detail} controlId={detail} userId={user?.id} canEdit onClose={() => setDetail(null)} onUnauthorized={onUnauthorized}
        onChanged={() => { loadControls(); if (data) loadInterventions(); }} />
    </div>
  );
}
